import React from 'react';
import { Switch as HeadlessSwitch } from '@headlessui/react';
import { FormField, isSome, noop } from '@crocodille/common';
import cn from 'classnames';

import styles from './switch.module.scss';

export interface SwitchProps {
  readonly value: boolean;
  readonly invalid?: boolean;
  onChange?(value: boolean): void;
  onBlur?(): void;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly invertedColors?: boolean;
  readonly name?: string;
  readonly isGray?: boolean;
}

/**
 * A switch-like checkbox element.
 */
export const Switch: React.FC<React.PropsWithChildren<SwitchProps>> = ({
  value,
  onChange = noop,
  onBlur = noop,
  className,
  disabled,
  invalid = false,
  invertedColors = false,
  name,
  isGray = false,
}) => {
  return (
    <HeadlessSwitch
      checked={value}
      onChange={onChange}
      onBlur={onBlur}
      className={cn(
        styles.container,
        {
          [styles.isChecked]: value,
          [styles.isDisabled]: disabled,
          [styles.isInvalid]: invalid,
          [styles.isInverted]: invertedColors,
          [styles.gray]: isGray,
        },
        className,
      )}
      disabled={disabled}
      name={name}
    >
      <span className={styles.knob}></span>
    </HeadlessSwitch>
  );
};

export function fieldToSwitchProps({
  value,
  onChange,
  onBlur,
  disabled,
  error,
}: FormField<boolean>): Pick<
  SwitchProps,
  'value' | 'onChange' | 'onBlur' | 'invalid' | 'disabled'
> {
  return {
    value,
    onChange,
    onBlur,
    disabled,
    invalid: isSome(error),
  };
}

export default Switch;
